import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  VStack,
  Flex,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useColorModeValue,
} from '@chakra-ui/react';
import {TablerModalCloseButton as ModalCloseButton} from './TablerCloseButtons';
import LanguageDialectPicker from './LanguageDialectPicker';
import {formatDobShort, truncateMrn} from '../../constants/designTokens';

// `overlayProps` / `contentProps` / `saveButtonProps` exist so each surface
// keeps its exact pre-merge appearance: the Hub renders the Chakra defaults
// with the `opalitePrimary` save button, while callers can still override
// overlay/content/button styling when they need a custom modal treatment.
//
// `duplicateMatch` (+ `onUseExisting` / `onCreateAnyway`) is create-flow only:
// when set, a warning banner offers the matched patient instead of a blind
// create. Edit flows simply never pass it.
const PatientFormModal = ({
  isOpen,
  onClose,
  title,
  formData,
  setFormData,
  onSave,
  saving,
  saveLabel,
  duplicateMatch = null,
  onUseExisting,
  onCreateAnyway,
  overlayProps = {},
  contentProps = {},
  saveButtonProps = {variant: 'opalitePrimary'},
}: Record<string, any>) => {
  const update = (field: any, value: any) =>
    setFormData((prev: any) => ({...prev, [field]: value}));

  const duplicateBg = useColorModeValue(
    'orange.50',
    'rgba(251, 211, 141, 0.12)',
  );
  const duplicateBorder = useColorModeValue('orange.200', 'orange.700');
  const duplicatePatient = duplicateMatch?.patient || null;
  const duplicateName = duplicatePatient
    ? [duplicatePatient.first_name, duplicatePatient.last_name]
        .map((part: any) => String(part || '').trim())
        .filter(Boolean)
        .join(' ')
    : '';
  const duplicateDetail = duplicatePatient
    ? [
        formatDobShort(duplicatePatient.date_of_birth)
          ? `DOB ${formatDobShort(duplicatePatient.date_of_birth)}`
          : '',
        duplicatePatient.mrn ? `MRN ${truncateMrn(duplicatePatient.mrn)}` : '',
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay {...overlayProps} />
      <ModalContent mx={4} {...contentProps}>
        <ModalHeader fontWeight="normal">{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={2}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" fontWeight="normal" color="gray.500">
              Required
            </Text>
            <Flex direction={{base: 'column', sm: 'row'}} gap={4} w="100%">
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="normal">
                  First Name
                </FormLabel>
                <Input
                  value={formData.firstName}
                  onChange={e => update('firstName', e.target.value)}
                  placeholder="First name"
                  borderRadius="lg"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="normal">
                  Last Name
                </FormLabel>
                <Input
                  value={formData.lastName}
                  onChange={e => update('lastName', e.target.value)}
                  placeholder="Last name"
                  borderRadius="lg"
                />
              </FormControl>
            </Flex>

            <Text fontSize="sm" fontWeight="normal" color="gray.500" pt={2}>
              Optional
            </Text>
            <LanguageDialectPicker
              selectedLanguage={formData.preferredLanguage}
              selectedDialect={formData.preferredDialect}
              onLanguageChange={(lang: any) =>
                setFormData((prev: any) => ({
                  ...prev,
                  preferredLanguage: lang,
                  preferredDialect: '',
                }))
              }
              onDialectChange={(dialect: any) =>
                update('preferredDialect', dialect)
              }
            />

            <Flex direction={{base: 'column', sm: 'row'}} gap={4} w="100%">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="normal">
                  MRN
                </FormLabel>
                <Input
                  value={formData.mrn}
                  onChange={e => update('mrn', e.target.value)}
                  placeholder="Medical record #"
                  borderRadius="lg"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="normal">
                  Gender
                </FormLabel>
                <Select
                  value={formData.gender}
                  onChange={e => update('gender', e.target.value)}
                  placeholder="Select"
                  borderRadius="lg"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </Select>
              </FormControl>
            </Flex>

            <Flex direction={{base: 'column', sm: 'row'}} gap={4} w="100%">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="normal">
                  Date of Birth
                </FormLabel>
                <Input
                  value={formData.dateOfBirth}
                  onChange={e => update('dateOfBirth', e.target.value)}
                  borderRadius="lg"
                  type="date"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="normal">
                  Phone
                </FormLabel>
                <Input
                  value={formData.phoneNumber}
                  onChange={e => update('phoneNumber', e.target.value)}
                  placeholder="Phone number"
                  borderRadius="lg"
                  type="tel"
                />
              </FormControl>
            </Flex>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="normal">
                Notes
              </FormLabel>
              <Textarea
                value={formData.notes}
                onChange={e => update('notes', e.target.value)}
                placeholder="Provider notes..."
                borderRadius="lg"
                rows={3}
                resize="vertical"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        {duplicatePatient ? (
          <Box
            data-testid="patient-duplicate-warning"
            role="alert"
            mx={6}
            mt={2}
            px={4}
            py={3}
            bg={duplicateBg}
            borderWidth="1px"
            borderColor={duplicateBorder}
            borderRadius="lg"
          >
            <Text fontSize="sm">
              A patient named <strong>{duplicateName}</strong>
              {duplicateDetail ? ` (${duplicateDetail})` : ''} already exists.
            </Text>
            <Flex gap={2} mt={3} wrap="wrap">
              <Button
                size="sm"
                variant="opalitePrimary"
                borderRadius="lg"
                onClick={onUseExisting}
                isDisabled={saving}
              >
                Use Existing
              </Button>
              <Button
                size="sm"
                variant="outline"
                borderRadius="lg"
                onClick={onCreateAnyway}
                isDisabled={saving}
              >
                Create Anyway
              </Button>
            </Flex>
          </Box>
        ) : null}
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} borderRadius="lg">
            Cancel
          </Button>
          <Button
            onClick={onSave}
            isLoading={saving}
            loadingText="Saving..."
            {...saveButtonProps}
          >
            {saveLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PatientFormModal;
